import { FC, useEffect, useState } from 'react';
import { EllipsisPopout, Icon, Checkbox, NextReviewLabel } from 'src/components';
import { defineMessages, useIntl } from 'react-intl';
import { DropdownSectionProps } from 'src/components/DropdownMenu';
import theme from 'src/utils/theme';
import { useMediaQuery } from 'react-responsive';
import { Creator } from 'src/api/wordlists/types';
import { UserDictionaryApi } from 'src/api/userDictionary';
import { TrackingApi } from 'src/api';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { WordDetailsModal } from 'src/apps/ScenarioGeneric/WordDetailsModal';
import { getLocalStorageEntry } from 'src/utils/webStorageUtils';
import { SOURCE_LANGUAGE, TARGET_LANGUAGE } from 'src/utils/language/saveLastLanguagePair';
import { useAudioPlayback } from 'src/context/AudioContext';
import { WordlistsApi } from 'src/api/wordlists';
import { rtlDirectionForLocale } from 'src/utils/rtlLocales';
import { ToastTypes } from 'src/apps/Generic/WordRowList';
import { useUserContext } from 'src/context/user';
import { resolveLearnableImageUrl } from 'src/utils/learnableMedia';
import { SelectionState, SelectionActions } from 'src/types/selection';
import { WordlistWord } from '../WordlistDetailsPage';
import * as S from './styles';

const msgs = defineMessages({
  markAsKnown: { id: 'scenarioDetails_markWordAsKnown' },
  unmarkAsKnown: { id: 'scenarioDetails_unmarkWordAsKnown' },
  markAsDifficult: { id: 'scenarioDetails_markWordAsDifficult' },
  unmarkAsDifficult: { id: 'scenarioDetails_removeWordAsDifficult' },
  wordInformation: { id: 'words_detail_word_info' },
  removeFromWordlist: { id: 'wordlist_ellipses_action_4' },
  allMarkedAsKnown: { id: 'learnq_previewScreen_learn_markedAsKnown' },
  audio: { id: 'words_detail_audio' },
});

interface Props {
  word: WordlistWord;
  options?: boolean;
  isSingleColumn?: boolean;
  wordlistCreator?: Creator;
  hideDifficultToggle?: boolean;
  hideMarkAsKnownToggle?: boolean;
  hideWordInfoToggle?: boolean;
  refetchLearnables?: () => void;
  onWordMarkedAsKnown?: (wordId: number) => void;
  difficult?: boolean;
  known?: boolean;
  handleShowToast?: (type: ToastTypes) => void;
  sectionType?: 'learned' | 'toLearn';
  selectionState?: SelectionState;
  selectionActions?: SelectionActions;
  allWordIds?: number[];
  wordlistId?: string;
}

export const WordRow: FC<Props> = ({
  word,
  options,
  isSingleColumn,
  wordlistCreator,
  hideDifficultToggle,
  hideMarkAsKnownToggle,
  hideWordInfoToggle,
  refetchLearnables,
  onWordMarkedAsKnown,
  handleShowToast,
  difficult,
  known,
  sectionType,
  selectionState,
  selectionActions,
  allWordIds,
  wordlistId,
}) => {
  const intl = useIntl();
  const languagePairId = getLanguagePairId();
  const isMobile = useMediaQuery({ maxWidth: theme.sz.m });
  const sourceLanguage = getLocalStorageEntry(SOURCE_LANGUAGE) ?? '';
  const targetLanguage = getLocalStorageEntry(TARGET_LANGUAGE) ?? '';

  const sourceLanguageDir = rtlDirectionForLocale(sourceLanguage);
  const targetLanguageDir = rtlDirectionForLocale(targetLanguage);
  const isSourceRTL = sourceLanguageDir === 'rtl';
  const isTargetRTL = targetLanguageDir === 'rtl';

  const { user } = useUserContext();

  const [isMarkedAsKnown, setAsKnown] = useState(known ?? false);
  const [isMarkedAsDifficult, setAsDifficult] = useState(difficult ?? false);
  const [isWordDetailsModalOpen, setIsWordDetailsModalOpen] = useState(false);
  const [playingAudioSrc, setPlayingAudioSrc] = useState<string | null>(null);
  const { play, stopAll, playing } = useAudioPlayback(playingAudioSrc);
  const startAudio = word.audioUrl ? () => setPlayingAudioSrc(word.audioUrl as string) : undefined;

  useEffect(() => {
    if (playingAudioSrc) {
      void play();
    }
  }, [playingAudioSrc, play]);

  useEffect(() => {
    if (!playing) {
      setPlayingAudioSrc(null);
    }
  }, [playing]);

  useEffect(() => {
    setAsKnown(known ?? false);
  }, [known]);

  useEffect(() => {
    setAsDifficult(difficult ?? false);
  }, [difficult]);

  const isPlayingAudio = playing && playingAudioSrc === word.audioUrl;
  const isSelected = selectionState?.selectedWordIds.has(word.id) ?? false;

  const handleSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectionActions || !sectionType || !allWordIds) return;

    const isShiftClick = event.nativeEvent instanceof MouseEvent && event.nativeEvent.shiftKey;
    selectionActions.toggleSelection(word.id, isShiftClick, allWordIds, sectionType);
  };

  const imageUrl = resolveLearnableImageUrl(word.targetValue);
  const targetContent = imageUrl ? (
    <S.TargetImage src={imageUrl} alt={word.sourceValue} />
  ) : (
    word.targetValue
  );

  const nextReviewLabel = word.nextReview ? <NextReviewLabel nextReview={word.nextReview} /> : null;

  const sourceColumn = (
    <S.SourceColumn>
      <S.SourceWord dir={sourceLanguageDir}>{word.sourceValue}</S.SourceWord>
      {nextReviewLabel}
    </S.SourceColumn>
  );

  const styledProps = {
    isIconOnRight: true,
    fontSize: theme.DEPRECATED_ty.medium,
    fontWeight: theme.DEPRECATED_ty.mediumWeight,
  };

  const handleIgnoreToggle = async () => {
    try {
      if (isMarkedAsKnown) {
        languagePairId &&
          (await UserDictionaryApi.updateSingleLearnable(languagePairId, word.id, {
            known: false,
            reset_growth_level: true,
          }));
        handleShowToast?.('unmarkedAsKnown');
      } else {
        /* istanbul ignore else: empty else branch */
        if (languagePairId) {
          await UserDictionaryApi.updateSingleLearnable(languagePairId, word.id, {
            known: true,
          });
          handleShowToast?.('asKnown');
        }
        onWordMarkedAsKnown?.(word.id);
      }
      setAsKnown(!isMarkedAsKnown);
      refetchLearnables && refetchLearnables();
    } catch (error) {
      console.error({ error });
    }
  };

  const handleOpenWordDetails = () => {
    /* istanbul ignore next: analytics */
    void TrackingApi.trackUserAction(
      'clicked_learnable_row',
      'wordlist_details_screen',
      languagePairId,
      !!user?.isPro,
      { id: String(word.id), ...(wordlistId && { wordlist_id: String(wordlistId) }) },
    );
    setIsWordDetailsModalOpen(true);
  };

  const handleDifficultToggle = async () => {
    try {
      if (isMarkedAsDifficult) {
        languagePairId &&
          (await UserDictionaryApi.updateSingleLearnable(languagePairId, word.id, {
            difficult: false,
          }));
      } else {
        languagePairId &&
          (await UserDictionaryApi.updateSingleLearnable(languagePairId, word.id, {
            difficult: true,
          }));
      }
      setAsDifficult(!isMarkedAsDifficult);
      handleShowToast?.('asDifficult');
    } catch (error) {
      console.error({ error });
    }
  };

  const dropdownSections = [
    ...(hideWordInfoToggle
      ? []
      : [
          {
            dropdownItems: [
              {
                type: 'icon',
                iconCode: 'sentencesOutline',
                labelText: intl.formatMessage(msgs.wordInformation),
                onClick: handleOpenWordDetails,
                ...styledProps,
              },
            ],
          },
        ]),
    ...(hideMarkAsKnownToggle
      ? []
      : [
          {
            dropdownItems: [
              {
                type: 'icon',
                iconCode: isMarkedAsKnown ? 'tickFilled' : 'tickOutline',
                labelText: intl.formatMessage(
                  isMarkedAsKnown ? msgs.unmarkAsKnown : msgs.markAsKnown,
                ),
                onClick: handleIgnoreToggle,
                ...styledProps,
              },
            ],
          },
        ]),
    ...(isMarkedAsKnown || hideDifficultToggle
      ? []
      : [
          {
            dropdownItems: [
              {
                type: 'icon',
                iconCode: isMarkedAsDifficult ? 'difficultWordsFilled' : 'difficultWordsOutline',
                labelText: intl.formatMessage(
                  isMarkedAsDifficult ? msgs.unmarkAsDifficult : msgs.markAsDifficult,
                ),
                onClick: handleDifficultToggle,
                ...styledProps,
              },
            ],
          },
        ]),
    ...(wordlistCreator === 'user' && user?.id === word.wordlistCreatorId
      ? [
          {
            dropdownItems: [
              {
                type: 'icon',
                iconCode: 'deleteIcon',
                labelText: intl.formatMessage(msgs.removeFromWordlist),
                onClick: async () => {
                  try {
                    await WordlistsApi.removeLearnablesFromWordlist(languagePairId as number, [
                      word.id,
                    ]);
                    refetchLearnables && refetchLearnables();
                  } catch (error) {
                    console.error({ error });
                  }
                },
                ...styledProps,
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <>
      {isWordDetailsModalOpen && (
        <WordDetailsModal
          onClose={() => setIsWordDetailsModalOpen(false)}
          isKnown={isMarkedAsKnown}
          isDifficult={isMarkedAsDifficult}
          hideDifficultToggle={hideDifficultToggle}
          hideMarkAsKnownToggle={hideMarkAsKnownToggle}
          onMarkAsKnown={handleIgnoreToggle}
          onMarkAsDifficult={handleDifficultToggle}
          targetLanguageName={targetLanguage}
          sourceLanguageName={sourceLanguage}
          learnableId={word.id}
        />
      )}
      <S.Container $isRTL={isSourceRTL || isTargetRTL} $isSingleColumn={isSingleColumn}>
        {isMobile || isSingleColumn ? (
          <S.WordInfo data-testid="mobile-word-row">
            <S.TargetWord dir={targetLanguageDir}>{targetContent}</S.TargetWord>
            <S.SourceWord $isSmall={isSingleColumn} dir={sourceLanguageDir}>
              {word.sourceValue}
            </S.SourceWord>
            {nextReviewLabel}
          </S.WordInfo>
        ) : (
          <>
            {(isTargetRTL && !isSourceRTL) || (isSourceRTL && !isTargetRTL) ? (
              <>
                {sourceColumn}
                <S.TargetWord dir={targetLanguageDir}>{targetContent}</S.TargetWord>
              </>
            ) : (
              <>
                <S.TargetWord dir={targetLanguageDir}>{targetContent}</S.TargetWord>
                {sourceColumn}
              </>
            )}
          </>
        )}
        <S.Actions>
          {word.audioUrl && (
            <S.AudioButton
              onClick={isPlayingAudio ? stopAll : startAudio}
              $isPlaying={isPlayingAudio}
              data-testid="audio-button"
              aria-label={intl.formatMessage(msgs.audio)}
            >
              <Icon name={isPlayingAudio ? 'wave' : 'speakerVolume3'} />
            </S.AudioButton>
          )}
          {options && selectionActions && (
            <Checkbox
              checked={isSelected}
              onChange={handleSelectionChange}
              ariaText="Select word"
              data-testid="word-selection-checkbox"
            />
          )}
          {options && (
            <EllipsisPopout
              data-testid="word-row-options"
              positionX="left"
              positionY="bottom"
              dropdownSections={dropdownSections as DropdownSectionProps[]}
            />
          )}
        </S.Actions>
      </S.Container>
    </>
  );
};
